/** Pure helpers for restoring contribution extra_fields into form state on edit. */

import { contributionFeatureShowsBusinessDetails } from './contributionPlaceAdmin';
import type { LaneDirection } from './road/laneFlow';
import { inferCarriagewayCountFromDetails, parseLaneDirections } from './road/laneFlow';
import {
  EMPTY_SIDE_FACILITIES,
  parseAccessTag,
  parseGutterTag,
  parseSidePlacement,
  parseStripKerbPlacement,
  parseSideFacilitySegmentsFromExtra,
  parseSideFacilityCondition,
  type SideFacilityFormState,
} from './roadSideFacilities';

export interface RoadExtraPatch {
  surface: string;
  smoothness: string;
  carriagewayCount: number;
  lanes: string;
  lanesForward: string;
  lanesBackward: string;
  laneDirections: LaneDirection[];
  manualLaneDirections: boolean;
  carriagewayALaneDirections: LaneDirection[];
  carriagewayBLaneDirections: LaneDirection[];
  width: string;
  carriagewayAWidth: string;
  carriagewayBWidth: string;
  toll: boolean;
  floodProne: boolean;
  potholes: boolean;
  streetVendors: boolean;
  accidentProne: boolean;
  hawkers: boolean;
  lit: boolean;
  drainage: boolean;
  maxspeed: string;
  crossingType: string;
  sideFacilities: SideFacilityFormState;
}

export interface AerowayExtraPatch {
  ref: string;
  surface: string;
  width: string;
}

export interface TransportStopExtraPatch {
  ref: string;
  routes: string[];
  operators: string[];
  shelter: boolean;
  bench: boolean;
  lighting: boolean;
}

export interface MotorwayJunctionExtraPatch {
  ref: string;
  exitTo?: string;
  exitToRoads?: Array<{ location_id?: string; name?: string }>;
}

export interface BusinessExtraPatch {
  cuisine: string;
  brand: string;
  wheelchair: string;
  paymentMobileMoney: boolean;
  paymentCard: boolean;
  mobileMoneyProvider: string;
}

export interface ContributionExtraPatch {
  landmarkSourceGroup: string | null;
  treeHeight: string;
  road?: RoadExtraPatch;
  aeroway?: AerowayExtraPatch;
  transportStop?: TransportStopExtraPatch;
  motorwayJunction?: MotorwayJunctionExtraPatch;
  business?: BusinessExtraPatch;
}

function str(value: unknown): string {
  return value == null || value === '' ? '' : String(value);
}

function bool(value: unknown, fallback = false): boolean {
  return value == null ? fallback : Boolean(value);
}

export function patchFromContributionExtras(
  featureType: string,
  extra: Record<string, unknown> | undefined,
): ContributionExtraPatch {
  const ef = extra ?? {};
  const landmarkSourceGroup =
    typeof ef.landmark_source_group === 'string' ? ef.landmark_source_group : null;

  const patch: ContributionExtraPatch = {
    landmarkSourceGroup,
    treeHeight: featureType === 'tree' ? str(ef.height) : '',
  };

  if (featureType === 'road') {
    let laneDirs = parseLaneDirections(ef.lane_directions);
    // Legacy: trace-direction flip stored as oneway_direction=reverse with all-forward lanes.
    if (ef.oneway_direction === 'reverse') {
      if (laneDirs.length > 0 && laneDirs.every((d) => d === 'forward')) {
        laneDirs = laneDirs.map(() => 'backward');
      } else if (laneDirs.length === 0) {
        const n = Math.max(1, parseInt(String(ef.lanes ?? '1'), 10) || 1);
        laneDirs = Array.from({ length: n }, () => 'backward');
      }
    }
    const dividedPair = Boolean(ef.divided_carriageway_pair);
    const aLaneDirs = parseLaneDirections(ef.carriageway_a_lane_directions);
    const bLaneDirs = parseLaneDirections(ef.carriageway_b_lane_directions);
    const carriagewayCount = inferCarriagewayCountFromDetails({
      carriageway_count: ef.carriageway_count != null ? Number(ef.carriageway_count) : null,
      divided_carriageway_pair: dividedPair,
      flow_mode: str(ef.flow_mode),
    });
    const legacyWidth = str(ef.width);
    const carriagewayAWidth = str(ef.carriageway_a_width) || (dividedPair ? legacyWidth : '');
    const carriagewayBWidth = str(ef.carriageway_b_width) || (dividedPair ? legacyWidth : '');
    patch.road = {
      surface: str(ef.surface),
      smoothness: str(ef.smoothness),
      carriagewayCount,
      lanes: str(ef.lanes),
      lanesForward: dividedPair ? str(ef.carriageway_a_lanes) : str(ef.lanes_forward),
      lanesBackward: dividedPair ? str(ef.carriageway_b_lanes) : str(ef.lanes_backward),
      laneDirections: laneDirs,
      manualLaneDirections: laneDirs.length > 0,
      carriagewayALaneDirections: aLaneDirs,
      carriagewayBLaneDirections: bLaneDirs,
      width: dividedPair ? '' : legacyWidth,
      carriagewayAWidth: dividedPair ? carriagewayAWidth : '',
      carriagewayBWidth: dividedPair ? carriagewayBWidth : '',
      toll: bool(ef.toll),
      floodProne: bool(ef.flood_prone),
      potholes: bool(ef.potholes),
      streetVendors: bool(ef.street_vendors),
      accidentProne: bool(ef.accident_prone),
      hawkers: bool(ef.hawkers),
      lit: bool(ef.lit),
      drainage: bool(ef.drainage),
      maxspeed: str(ef.maxspeed),
      crossingType: str(ef.crossing_type),
      sideFacilities: {
        ...EMPTY_SIDE_FACILITIES,
        sidewalk: dividedPair ? '' : parseSidePlacement(ef.sidewalk),
        cycleway: dividedPair ? '' : parseSidePlacement(ef.cycleway),
        shoulder: dividedPair ? '' : parseSidePlacement(ef.shoulder),
        parkingLane: dividedPair ? '' : parseSidePlacement(ef.parking_lane),
        foot: parseAccessTag(ef.foot),
        bicycle: parseAccessTag(ef.bicycle),
        bus: parseAccessTag(ef.bus),
        psv: parseAccessTag(ef.psv),
        access: parseAccessTag(ef.access),
        sidewalkCondition: parseSideFacilityCondition(ef.sidewalk_condition),
        cyclewayCondition: parseSideFacilityCondition(ef.cycleway_condition),
        shoulderCondition: parseSideFacilityCondition(ef.shoulder_condition),
        parkingLaneCondition: parseSideFacilityCondition(ef.parking_lane_condition),
        sidewalkMaxspeed: str(ef.sidewalk_maxspeed),
        cyclewayMaxspeed: str(ef.cycleway_maxspeed),
        shoulderMaxspeed: str(ef.shoulder_maxspeed),
        parkingLaneMaxspeed: str(ef.parking_lane_maxspeed),
        gutter: parseGutterTag(ef.gutter),
        sidewalkWidth: dividedPair ? '' : str(ef.sidewalk_width),
        cyclewayWidth: dividedPair ? '' : str(ef.cycleway_width),
        shoulderWidth: dividedPair ? '' : str(ef.shoulder_width),
        parkingLaneWidth: dividedPair ? '' : str(ef.parking_lane_width),
        sidewalkKerbGap: dividedPair ? '' : str(ef.sidewalk_kerb_gap),
        cyclewayKerbGap: dividedPair ? '' : str(ef.cycleway_kerb_gap),
        shoulderKerbGap: dividedPair ? '' : str(ef.shoulder_kerb_gap),
        parkingLaneKerbGap: dividedPair ? '' : str(ef.parking_lane_kerb_gap),
        carriagewayASidewalkWidth: str(ef.carriageway_a_sidewalk_width),
        carriagewayACyclewayWidth: str(ef.carriageway_a_cycleway_width),
        carriagewayAShoulderWidth: str(ef.carriageway_a_shoulder_width),
        carriagewayAParkingLaneWidth: str(ef.carriageway_a_parking_lane_width),
        carriagewayBSidewalkWidth: str(ef.carriageway_b_sidewalk_width),
        carriagewayBCyclewayWidth: str(ef.carriageway_b_cycleway_width),
        carriagewayBShoulderWidth: str(ef.carriageway_b_shoulder_width),
        carriagewayBParkingLaneWidth: str(ef.carriageway_b_parking_lane_width),
        carriagewayASidewalkKerbGap: str(ef.carriageway_a_sidewalk_kerb_gap),
        carriagewayACyclewayKerbGap: str(ef.carriageway_a_cycleway_kerb_gap),
        carriagewayAShoulderKerbGap: str(ef.carriageway_a_shoulder_kerb_gap),
        carriagewayAParkingLaneKerbGap: str(ef.carriageway_a_parking_lane_kerb_gap),
        carriagewayBSidewalkKerbGap: str(ef.carriageway_b_sidewalk_kerb_gap),
        carriagewayBCyclewayKerbGap: str(ef.carriageway_b_cycleway_kerb_gap),
        carriagewayBShoulderKerbGap: str(ef.carriageway_b_shoulder_kerb_gap),
        carriagewayBParkingLaneKerbGap: str(ef.carriageway_b_parking_lane_kerb_gap),
        sideFacilitySegments: dividedPair ? {} : parseSideFacilitySegmentsFromExtra(ef.side_facility_segments),
        carriagewayASegments: dividedPair
          ? parseSideFacilitySegmentsFromExtra(ef.carriageway_a_side_facility_segments)
          : {},
        carriagewayBSegments: dividedPair
          ? parseSideFacilitySegmentsFromExtra(ef.carriageway_b_side_facility_segments)
          : {},
        carriagewayASidewalk: dividedPair ? parseStripKerbPlacement(ef.carriageway_a_sidewalk) : '',
        carriagewayACycleway: dividedPair ? parseStripKerbPlacement(ef.carriageway_a_cycleway) : '',
        carriagewayAShoulder: dividedPair ? parseStripKerbPlacement(ef.carriageway_a_shoulder) : '',
        carriagewayAParkingLane: dividedPair ? parseStripKerbPlacement(ef.carriageway_a_parking_lane) : '',
        carriagewayBSidewalk: dividedPair ? parseStripKerbPlacement(ef.carriageway_b_sidewalk) : '',
        carriagewayBCycleway: dividedPair ? parseStripKerbPlacement(ef.carriageway_b_cycleway) : '',
        carriagewayBShoulder: dividedPair ? parseStripKerbPlacement(ef.carriageway_b_shoulder) : '',
        carriagewayBParkingLane: dividedPair ? parseStripKerbPlacement(ef.carriageway_b_parking_lane) : '',
      },
    };
  }

  if (featureType === 'aeroway') {
    patch.aeroway = {
      ref: str(ef.ref),
      surface: str(ef.surface),
      width: str(ef.width),
    };
  }

  if (featureType === 'transport_stop') {
    patch.transportStop = {
      ref: str(ef.ref),
      routes: Array.isArray(ef.routes) ? ef.routes.map(String) : [],
      operators: Array.isArray(ef.operators) ? ef.operators.map(String) : [],
      shelter: bool(ef.shelter),
      bench: bool(ef.bench),
      lighting: bool(ef.lighting),
    };
  }

  if (featureType === 'motorway_junction') {
    patch.motorwayJunction = {
      ref: str(ef.ref),
      exitTo: ef.exit_to ? String(ef.exit_to) : undefined,
      exitToRoads: Array.isArray(ef.exit_to_roads)
        ? (ef.exit_to_roads as Array<{ location_id?: string; name?: string }>)
        : undefined,
    };
  }

  if (contributionFeatureShowsBusinessDetails(featureType, landmarkSourceGroup)) {
    patch.business = {
      cuisine: str(ef.cuisine),
      brand: str(ef.brand),
      wheelchair: str(ef.wheelchair),
      paymentMobileMoney: bool(ef.payment_mobile_money),
      paymentCard: bool(ef.payment_card),
      mobileMoneyProvider: str(ef.mobile_money_provider),
    };
  }

  return patch;
}
